import React from 'react';
import styled, { useTheme } from 'styled-components';
import { Inline } from '../Inline';
import { Stack } from '../Stack';
import { Styles as BaseStyles } from '../Styles';
import { ButtonGroupContext } from './Context';
import { Link } from '../Action';
import { buildStylesBySize } from '../Action/shared';
import { useMediaSize } from '../tools/utils';
/**
 * This is the safest way to grab the type of a component since we use
 * react-hot-loader:
 * https://github.com/gaearon/react-hot-loader/issues/304#issuecomment-225023521
 *
 * The a11y error is disabled because we are only grabbing the type and do not
 * need a valid href
 */
// eslint-disable-next-line jsx-a11y/anchor-is-valid
const LinkType = (React.createElement(Link, { href: "", text: "" })).type;
export const isLinkVariant = (child) => {
    /**
     * `Link` components have a default variant of "link". However, if the
     * `variant` prop is not passed to a `Link` component, then it will not appear
     * in `child.props`. This check accounts for that scenario.
     */
    if (child.type === LinkType && !child.props.variant)
        return true;
    /**
     * TS can't be relied upon here to guarantee that `variant` will
     * exist as a child here despite passing `ButtonProps` as a generic
     * to `ReactElement`. See
     * https://stackoverflow.com/questions/44475309/how-do-i-restrict-the-type-of-react-children-in-typescript-using-the-newly-adde
     */
    return Object.entries(child.props).some(([key, value]) => {
        const isVariantProp = key === 'variant';
        const isValueLink = value === 'link';
        return isVariantProp && isValueLink;
    });
};
// Hack to make all children of items in button groups inherit the font-size of the item
// This is used in cases like a consumer using "Link" with a routing framework
const Styles = styled(BaseStyles) `
  && {
    & > * {
      font-size: inherit;
    }
  }
`;
const ButtonChild = ({ child, className, disableStackSmDown, stackMd, }) => {
    const theme = useTheme();
    if (isLinkVariant(child)) {
        const smDownWidths = disableStackSmDown
            ? ['fit-content', 'fit-content']
            : ['100%', '100%'];
        const mdWidth = stackMd ? '100%' : 'fit-content';
        const width = [...smDownWidths, mdWidth, 'fit-content'];
        // Link variants don't have styles for `sm` size, so we always use `md` styles
        const mdSizeStyles = buildStylesBySize(theme).md;
        return (React.createElement(Styles, { px: "xs", height: mdSizeStyles.height, boxSizing: "border-box", width: width, fontSize: mdSizeStyles.fontSize, display: "flex", justifyContent: "center", alignItems: "center", className: className }, child));
    }
    return React.createElement(Styles, { className: className }, child);
};
const ButtonGroup = ({ children, disableStackSmDown, stackMd, className, buttonSize = 'md', }) => {
    const isSmDown = useMediaSize('SmDown');
    const isMdOnly = useMediaSize('MdOnly');
    const isStackableMdOnly = stackMd && isMdOnly;
    const isStackableSmDown = !disableStackSmDown && isSmDown;
    const isStackable = isStackableMdOnly || isStackableSmDown;
    const filteredChildren = React.Children.toArray(children).filter(Boolean);
    const mappedChildren = React.Children.map(filteredChildren, child => {
        return React.createElement(ButtonChild, { child: child });
    });
    return (React.createElement(ButtonGroupContext.Provider, { value: { buttonSize, inButtonGroup: true } }, isStackable ? (React.createElement(Stack, { className: className, space: "md" }, mappedChildren)) : (React.createElement(Inline, { className: className, space: "md" }, mappedChildren))));
};
export { ButtonGroup };
//# sourceMappingURL=ButtonGroup.js.map